import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import '../index.css';

function AuditLogsPage() {
    const { hasRole } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [actionFilter, setActionFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');
    const [nameFilter, setNameFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [recordsPerPage, setRecordsPerPage] = useState(20);

    // Action types for filter
    const [actionTypes, setActionTypes] = useState([]);

    useEffect(() => {
        if (hasRole('admin')) {
            loadActionTypes();
        }
    }, []);

    useEffect(() => {
        if (hasRole('admin')) {
            loadLogs();
        }
    }, [actionFilter, roleFilter, nameFilter, startDate, endDate, currentPage, recordsPerPage]);

    const loadActionTypes = async () => {
        try {
            const response = await axios.get('http://localhost:5000/api/audit-logs/actions');
            setActionTypes(response.data || []);
        } catch (err) {
            console.error('Error loading action types:', err);
        }
    };

    const loadLogs = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage,
                limit: recordsPerPage
            });

            if (actionFilter !== 'all') params.append('action', actionFilter);
            if (roleFilter !== 'all') params.append('userRole', roleFilter);
            if (nameFilter) params.append('userName', nameFilter);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);

            const response = await axios.get(`http://localhost:5000/api/audit-logs?${params.toString()}`);
            setLogs(response.data.logs || []);
            setTotalPages(response.data.pagination?.totalPages || 1);
            setTotalCount(response.data.pagination?.totalCount || 0);
        } catch (err) {
            console.error('Error loading logs:', err);
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setActionFilter('all');
        setRoleFilter('all');
        setNameFilter('');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
    };

    const getActionColor = (action) => {
        if (action.includes('login')) return '#10b981';
        if (action.includes('created')) return '#3b82f6';
        if (action.includes('updated') || action.includes('submitted')) return '#f59e0b';
        if (action.includes('deleted') || action.includes('deactivated')) return '#ef4444';
        if (action.includes('activated')) return '#22c55e';
        if (action.includes('payment')) return '#8b5cf6';
        if (action.includes('so-created')) return '#06b6d4';
        return '#6b7280';
    };

    const getActionIcon = (action) => {
        if (action.includes('login')) return '🔑';
        if (action.includes('created')) return '➕';
        if (action.includes('updated')) return '✏️';
        if (action.includes('deleted')) return '🗑️';
        if (action.includes('submitted')) return '📤';
        if (action.includes('payment')) return '💳';
        if (action.includes('so-created')) return '📋';
        if (action.includes('activated')) return '✅';
        if (action.includes('deactivated')) return '⛔';
        return '📌';
    };

    if (!hasRole('admin')) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
                <h2>Access Denied</h2>
                <p>Only administrators can access audit logs.</p>
            </div>
        );
    }

    return (
        <div>
            <h1 className="page-title">📋 Audit Logs</h1>

            {/* Filters Card */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, marginBottom: '1rem', color: '#374151' }}>🔍 Filters</h3>
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    alignItems: 'end'
                }}>
                    {/* Action Type Filter */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                            Action Type
                        </label>
                        <select
                            value={actionFilter}
                            onChange={(e) => { setActionFilter(e.target.value); setCurrentPage(1); }}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                            }}
                        >
                            <option value="all">All Actions</option>
                            <option value="login">🔑 Logins</option>
                            <option value="user:created">👤 User Created</option>
                            <option value="user:deleted">🗑️ User Deleted</option>
                            <option value="user:role">🔄 Role Changes</option>
                            <option value="sales-order:created">📄 Sales Order Drafted</option>
                            <option value="sales-order:updated">✏️ Sales Order Updated</option>
                            <option value="sales-order:submitted">📤 Sales Order Submitted</option>
                            <option value="payment">💳 Payment Actions</option>
                            <option value="so-created">📋 SO Created</option>
                        </select>
                    </div>

                    {/* User Role Filter */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                            User Role
                        </label>
                        <select
                            value={roleFilter}
                            onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                            }}
                        >
                            <option value="all">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="invoice-editor">Invoice Editor</option>
                            <option value="normal-user">Normal User</option>
                        </select>
                    </div>

                    {/* User Name Filter */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                            User Name
                        </label>
                        <input
                            type="text"
                            value={nameFilter}
                            onChange={(e) => { setNameFilter(e.target.value); setCurrentPage(1); }}
                            placeholder="Search by name..."
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                            }}
                        />
                    </div>

                    {/* Start Date */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                            From Date
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                            }}
                        />
                    </div>

                    {/* End Date */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                            To Date
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #ddd'
                            }}
                        />
                    </div>

                    {/* Clear Filters Button */}
                    <div>
                        <button
                            onClick={clearFilters}
                            style={{
                                width: '100%',
                                padding: '0.5rem 1rem',
                                background: '#f3f4f6',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            ✖ Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <div className="card">
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                }}>
                    <span style={{ color: '#6b7280' }}>
                        Showing {logs.length} of {totalCount} records
                    </span>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>Loading logs...</div>
                ) : logs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                        No audit logs found
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="product-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    <th>User</th>
                                    <th>Target</th>
                                    <th>Details</th>
                                    <th>Timestamp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.id}>
                                        <td>
                                            <span style={{
                                                padding: '0.35rem 0.75rem',
                                                borderRadius: '12px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                background: getActionColor(log.action) + '20',
                                                color: getActionColor(log.action),
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.35rem'
                                            }}>
                                                {getActionIcon(log.action)} {log.action}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                                {log.user?.name || log.performedByName || 'System'}
                                            </div>
                                            {(log.user?.role || log.performedByRole) && (
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    color: '#6b7280',
                                                    marginTop: '0.25rem'
                                                }}>
                                                    {log.user?.role || log.performedByRole}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {log.targetType && (
                                                <div>
                                                    <div style={{ fontWeight: '600', color: '#374151' }}>
                                                        {log.targetType}
                                                    </div>
                                                    {log.targetId && (
                                                        <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'monospace' }}>
                                                            {log.targetId.toString().substring(0, 12)}...
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {log.details && (
                                                <details>
                                                    <summary style={{ cursor: 'pointer', color: '#667eea', fontWeight: '600' }}>
                                                        View Details
                                                    </summary>
                                                    <pre style={{
                                                        background: '#f9fafb',
                                                        padding: '0.5rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        marginTop: '0.5rem',
                                                        overflow: 'auto',
                                                        maxWidth: '300px'
                                                    }}>
                                                        {JSON.stringify(log.details, null, 2)}
                                                    </pre>
                                                </details>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ fontSize: '0.9rem', color: '#374151' }}>
                                                {log.timestampIST || new Date(log.timestamp).toLocaleDateString()}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                                                {/* If server provided a single display string, don't duplicate time */}
                                                {!log.timestampIST && new Date(log.timestamp).toLocaleTimeString()}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 0 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '1.5rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid #e5e7eb'
                    }}>
                        {/* Records per page */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.9rem', color: '#6b7280' }}>Records per page:</label>
                            <select
                                value={recordsPerPage}
                                onChange={(e) => { setRecordsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}
                                style={{
                                    padding: '0.35rem 0.75rem',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd'
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>

                        {/* Page navigation */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '0.35rem 0.75rem',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    background: currentPage === 1 ? '#f3f4f6' : 'white',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ⟨⟨
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '0.35rem 0.75rem',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    background: currentPage === 1 ? '#f3f4f6' : 'white',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ⟨ Prev
                            </button>

                            <span style={{ padding: '0 1rem', color: '#374151' }}>
                                Page {currentPage} of {totalPages}
                            </span>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                style={{
                                    padding: '0.35rem 0.75rem',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    background: currentPage === totalPages ? '#f3f4f6' : 'white',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Next ⟩
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                style={{
                                    padding: '0.35rem 0.75rem',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    background: currentPage === totalPages ? '#f3f4f6' : 'white',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ⟩⟩
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AuditLogsPage;
